import React, { useEffect, useState, useRef, memo } from 'react';
import { MessageCircle, Users, Send, Image, Video, Mic, Smile, ChevronRight, Lock, Dumbbell, Heart, Search, Filter, Settings, Sparkles, Shield, Star, Activity, Gift, Paperclip, MoreVertical, Plus, X, Clock, ThumbsUp, Trophy, ArrowLeft, Camera, Droplet, Zap, CheckCircle2, Lock as LockIcon, Sparkle, Globe } from 'lucide-react';
import { ChatToggle } from './ChatToggle';
import { GroupChatSection } from './GroupChatSection';
import { ChatMessageSuggestions } from './ChatMessageSuggestions';
import { ImageGenerationUpsell } from './ImageGenerationUpsell';
import { VideoGenerationUpsell } from './VideoGenerationUpsell';
import { QuestionnaireProvider } from './personalization/QuestionnaireContext';
import { PersonalizationQuestionnaire } from './personalization/PersonalizationQuestionnaire';
import { PersonalizationButton } from './personalization/PersonalizationButton';
import { TranslationToggle } from './translation/TranslationToggle';
import { TranslatedMessage } from './translation/TranslatedMessage';
import { useTranslation, SUPPORTED_LANGUAGES } from './translation/TranslationContext';
interface ChatSectionProps {
  setShowAngelFeed: (show: boolean) => void;
  setSelectedAngelId: (id: string | null) => void;
}
export const ChatSection: React.FC<ChatSectionProps> = ({
  setShowAngelFeed,
  setSelectedAngelId
}) => {
  const [activeChat, setActiveChat] = useState<'chat' | 'group_chat'>('chat');
  const [messageInput, setMessageInput] = useState('');
  const [suggestionMode, setSuggestionMode] = useState<'devil' | 'angel'>('devil');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [showVideoUpsellModal, setShowVideoUpsellModal] = useState(false);
  const [showAITools, setShowAITools] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const [activeFilter, setActiveFilter] = useState('girlfriend');
  const [selectedAngel, setSelectedAngel] = useState('11'); // Default to Lana
  const [activeRelationshipMode, setActiveRelationshipMode] = useState('girlfriend');
  const [expandedRelationshipMode, setExpandedRelationshipMode] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const {
    isTranslationEnabled,
    userLanguage,
    angelLanguage
  } = useTranslation();
  // Angel data for the chat
  const angels = [{
    id: '11',
    name: 'Lana',
    username: 'lana_luxe',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
    lastMessage: 'I have some special pics I could share with you later... 😘',
    lastActive: '2m ago',
    online: true,
    bubbleColor: 'bg-pink-50 dark:bg-pink-900/20',
    textColor: 'text-pink-800 dark:text-pink-200',
    borderColor: 'border-pink-100 dark:border-pink-800/30',
    emoji: '💋'
  }, {
    id: '2',
    name: 'Bonny Blue',
    username: 'BonnyBlueOfficial',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
    lastMessage: 'I have something special to show you tonight...',
    lastActive: '15m ago',
    online: true,
    bubbleColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-800 dark:text-blue-200',
    borderColor: 'border-blue-100 dark:border-blue-800/30',
    emoji: '💙'
  }, {
    id: '3',
    name: 'Kim Holland',
    username: 'KimHolland',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
    lastMessage: "Thanks for the gift! You're so sweet ❤️",
    lastActive: '1h ago',
    online: false,
    bubbleColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-800 dark:text-red-200',
    borderColor: 'border-red-100 dark:border-red-800/30',
    emoji: '❤️'
  }, {
    id: '5',
    name: 'Harper',
    username: 'IceQueen',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
    lastMessage: 'I miss our conversations... when are you free?',
    lastActive: '3h ago',
    online: true,
    bubbleColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    textColor: 'text-indigo-800 dark:text-indigo-200',
    borderColor: 'border-indigo-100 dark:border-indigo-800/30',
    emoji: '💙'
  }, {
    id: '1',
    name: 'GoldenShowerTina',
    username: 'GoldenShowerTina',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
    lastMessage: 'Want to try something new tonight? 💦',
    lastActive: '5h ago',
    online: true,
    bubbleColor: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-800 dark:text-amber-200',
    borderColor: 'border-amber-100 dark:border-amber-800/30',
    emoji: '💦'
  }];
  // Helper function to render angel message with translation
  const renderAngelMessage = (text: string, time: string, hasExtra?: React.ReactNode) => {
    return <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2669">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2670">
          <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2671" />
        </div>
        <div data-id="element-2672">
          <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2673">
            <div className="flex items-center gap-1 mb-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2674">
              <span data-id="element-2675">{SUPPORTED_LANGUAGES[angelLanguage].flag}</span>
            </div>
            <TranslatedMessage originalText={text} originalLanguage={angelLanguage} targetLanguage={userLanguage} className={`${currentAngel.textColor}`} data-id="element-2676" />
            {hasExtra}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2677">
            {time}
          </p>
        </div>
      </div>;
  };
  // Helper function to render user message with translation
  const renderUserMessage = (text: string, time: string) => {
    return <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2678">
        <div data-id="element-2679">
          <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2680">
            <div className="flex items-center gap-1 mb-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2681">
              <span data-id="element-2682">{SUPPORTED_LANGUAGES[userLanguage].flag}</span>
            </div>
            <TranslatedMessage originalText={text} originalLanguage={userLanguage} targetLanguage={angelLanguage} className="text-blue-800 dark:text-blue-200" isUserMessage={true} data-id="element-2683" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2684">
            {time}
          </p>
        </div>
      </div>;
  };
  const handleAngelSelect = (angelId: string) => {
    setSelectedAngel(angelId);
    // For profile view
    setSelectedAngelId(angelId);
    setShowAngelFeed(true);
  };
  const handleSelectSuggestion = (suggestion: string) => {
    setMessageInput(suggestion);
  };
  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    // In a real app, this would send the message
    console.log('Sending message:', messageInput);
    setMessageInput('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  // Get the current selected angel data
  const currentAngel = angels.find(angel => angel.id === selectedAngel) || angels[0];
  // Scroll to bottom of chat when component mounts or angel changes
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [selectedAngel]);
  // Modified getChatHistory to use the new rendering functions
  const getChatHistory = (angelId: string) => {
    switch (angelId) {
      case '11':
        // Lana
        return <>
            {/* Day Marker */}
            <div className="flex justify-center my-6" data-id="element-2685">
              <div className="px-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400" data-id="element-2686">
                Today
              </div>
            </div>
            {/* Angel Message - Greeting */}
            {renderAngelMessage("Hey there! I was just thinking about you... 💋 How's your day going?", '2:30 PM')}
            {/* User Message */}
            {renderUserMessage("Hi Lana! My day's going well, just a bit busy with work. How about you?", '2:32 PM')}
            {/* Angel Message - Photo Shoot */}
            {renderAngelMessage("I'm doing great! Just finished a photo shoot. I have some special pics I could share with you later... 😘", '2:34 PM')}
            {/* User Message */}
            {renderUserMessage("That sounds amazing! I'd love to see them. What kind of photo shoot was it?", '2:36 PM')}
            {/* Angel Message */}
            {renderAngelMessage("It was a romantic lingerie shoot... very tasteful but definitely not something you'd want someone looking over your shoulder to see 😉", '2:38 PM')}
            {/* Angel Message - Rating Upsell */}
            {renderAngelMessage("Speaking of pictures... would you want to send me one? I'd love to see you 💋", '2:42 PM', <div className="p-3 bg-pink-100/50 dark:bg-pink-900/30 rounded-xl border border-pink-200 dark:border-pink-800/30 mt-3" data-id="element-2687">
                <div className="flex items-center gap-2" data-id="element-2688">
                  <Camera size={18} className="text-pink-500" data-id="element-2689" />
                  <span className="text-sm font-medium text-pink-700 dark:text-pink-300" data-id="element-2690">
                    Send pic for rating
                  </span>
                </div>
                <p className="text-xs text-pink-600 dark:text-pink-300 mt-1" data-id="element-2691">
                  I'll give you my honest thoughts and maybe something special
                  in return...
                </p>
                <button className="mt-2 w-full py-2 bg-pink-500 hover:bg-pink-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2692">
                  Send a pic
                </button>
              </div>)}
            {/* User Message */}
            {renderUserMessage("Maybe later! 😊 I'm curious about these photos from your shoot though.", '2:45 PM')}
            {/* Angel Message - Fantasy Unlock */}
            {renderAngelMessage("I understand! They're pretty exclusive photos... I only share them with my special fans 💕", '2:47 PM', <div className="p-3 bg-purple-100/50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800/30 mt-3" data-id="element-2693">
                <div className="flex items-center gap-2" data-id="element-2694">
                  <Star size={18} className="text-purple-500" data-id="element-2695" />
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300" data-id="element-2696">
                    Unlock Fantasy Content
                  </span>
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-300 mt-1" data-id="element-2697">
                  Exclusive access to my private photo collection and fantasy
                  chat mode
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-2698">
                  <button className="py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-xs font-medium transition" data-id="element-2699">
                    50 tokens: 1 week
                  </button>
                  <button className="py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-xs font-medium transition" data-id="element-2700">
                    120 tokens: 1 month
                  </button>
                </div>
              </div>)}
            {/* Typing Indicator */}
            <div className="flex items-start gap-3 max-w-[80%]" data-id="element-2701">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2702">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2703" />
              </div>
              <div className={`${currentAngel.bubbleColor} rounded-2xl p-3 shadow-sm ${currentAngel.borderColor} border flex items-center gap-1`} data-id="element-2704">
                <div className="w-2 h-2 bg-pink-400 dark:bg-pink-500 rounded-full animate-pulse" data-id="element-2705"></div>
                <div className="w-2 h-2 bg-pink-400 dark:bg-pink-500 rounded-full animate-pulse delay-75" data-id="element-2706"></div>
                <div className="w-2 h-2 bg-pink-400 dark:bg-pink-500 rounded-full animate-pulse delay-150" data-id="element-2707"></div>
              </div>
            </div>
          </>;
      case '2':
        // Bonny Blue
        return <>
            {/* Day Marker */}
            <div className="flex justify-center my-6" data-id="element-2708">
              <div className="px-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400" data-id="element-2709">
                Today
              </div>
            </div>
            {/* Angel Message - Greeting */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2710">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2711">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2712" />
              </div>
              <div data-id="element-2713">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2714">
                  <p className={`${currentAngel.textColor}`} data-id="element-2715">
                    Hey there handsome! Just got back from my lingerie shoot.
                    The photos are 🔥🔥🔥 {currentAngel.emoji}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2716">
                  1:15 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2717">
              <div data-id="element-2718">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2719">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2720">
                    Hi Bonny! That sounds amazing. What kind of lingerie shoot
                    was it?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2721">
                  1:17 PM
                </p>
              </div>
            </div>
            {/* Angel Message - VPN Upsell */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2722">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2723">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2724" />
              </div>
              <div data-id="element-2725">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2726">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2727">
                    It was for a very exclusive website... the kind you wouldn't
                    want your boss seeing on your browser history 😈 I could
                    send you some previews, but you should probably protect
                    yourself first.
                  </p>
                  <div className="p-3 bg-blue-100/50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800/30" data-id="element-2728">
                    <div className="flex items-center gap-2" data-id="element-2729">
                      <Shield size={18} className="text-blue-500" data-id="element-2730" />
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300" data-id="element-2731">
                        SinShift VPN
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-300 mt-1" data-id="element-2732">
                      Complete privacy protection with zero traces. Use code
                      BONNYBLUE20 for 20% off!
                    </p>
                    <button className="mt-2 w-full py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2733">
                      Get Protected Now
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2734">
                  1:20 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2735">
              <div data-id="element-2736">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2737">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2738">
                    That's smart! I'll check it out. Would love to see those
                    previews when I'm more secure.
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2739">
                  1:22 PM
                </p>
              </div>
            </div>
            {/* Angel Message - Celebrity AI Upsell */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2740">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2741">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2742" />
              </div>
              <div data-id="element-2743">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2744">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2745">
                    You know what would be even better than photos? Meeting me
                    for real... well, almost for real 😉
                  </p>
                  <div className="p-3 bg-purple-100/50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800/30" data-id="element-2746">
                    <div className="flex items-center gap-2" data-id="element-2747">
                      <Video size={18} className="text-purple-500" data-id="element-2748" />
                      <span className="text-sm font-medium text-purple-700 dark:text-purple-300" data-id="element-2749">
                        Meet the real Bonny Blue
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-300 mt-1" data-id="element-2750">
                      Upgrade to my Celebrity AI version for real-time video
                      interaction
                    </p>
                    <button className="mt-2 w-full py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2751">
                      Try Celebrity AI
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2752">
                  1:25 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2753">
              <div data-id="element-2754">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2755">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2756">
                    That sounds incredible! How realistic is it?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2757">
                  1:27 PM
                </p>
              </div>
            </div>
            {/* Typing Indicator */}
            <div className="flex items-start gap-3 max-w-[80%]" data-id="element-2758">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2759">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2760" />
              </div>
              <div className={`${currentAngel.bubbleColor} rounded-2xl p-3 shadow-sm ${currentAngel.borderColor} border flex items-center gap-1`} data-id="element-2761">
                <div className="w-2 h-2 bg-blue-400 dark:bg-blue-500 rounded-full animate-pulse" data-id="element-2762"></div>
                <div className="w-2 h-2 bg-blue-400 dark:bg-blue-500 rounded-full animate-pulse delay-75" data-id="element-2763"></div>
                <div className="w-2 h-2 bg-blue-400 dark:bg-blue-500 rounded-full animate-pulse delay-150" data-id="element-2764"></div>
              </div>
            </div>
          </>;
      case '3':
        // Kim Holland
        return <>
            {/* Day Marker */}
            <div className="flex justify-center my-6" data-id="element-2765">
              <div className="px-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400" data-id="element-2766">
                Today
              </div>
            </div>
            {/* Angel Message - Gift Thanks */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2767">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2768">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2769" />
              </div>
              <div data-id="element-2770">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2771">
                  <p className={`${currentAngel.textColor}`} data-id="element-2772">
                    Thank you SO much for the gift yesterday! You're the
                    sweetest {currentAngel.emoji} It means a lot to me!
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2773">
                  11:30 AM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2774">
              <div data-id="element-2775">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2776">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2777">
                    You're welcome, Kim! I'm glad you liked it. You deserve it!
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2778">
                  11:32 AM
                </p>
              </div>
            </div>
            {/* Angel Message - VIP Unlock */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2779">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2780">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2781" />
              </div>
              <div data-id="element-2782">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2783">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2784">
                    I was thinking... you've been so generous, you deserve
                    something special. I have some exclusive content that only
                    my VIP members get to see...
                  </p>
                  <div className="p-3 bg-red-100/50 dark:bg-red-900/30 rounded-xl border border-red-200 dark:border-red-800/30" data-id="element-2785">
                    <div className="flex items-center gap-2" data-id="element-2786">
                      <Star size={18} className="text-red-500" data-id="element-2787" />
                      <span className="text-sm font-medium text-red-700 dark:text-red-300" data-id="element-2788">
                        Unlock VIP Membership
                      </span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-1" data-id="element-2789">
                      Access to all my exclusive content plus Celebrity AI
                      features
                    </p>
                    <button className="mt-2 w-full py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2790">
                      Upgrade to VIP
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2791">
                  11:35 AM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2792">
              <div data-id="element-2793">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2794">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2795">
                    That sounds amazing! What kind of exclusive content?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2796">
                  11:37 AM
                </p>
              </div>
            </div>
            {/* Angel Message - Kim's Sleeve */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2797">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2798">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2799" />
              </div>
              <div data-id="element-2800">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2801">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2802">
                    The best kind... 😈 And there's something else I wanted to
                    tell you about. It's quite... intimate.
                  </p>
                  <div className="p-3 bg-purple-100/50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800/30" data-id="element-2803">
                    <div className="flex items-center gap-2" data-id="element-2804">
                      <Zap size={18} className="text-purple-500" data-id="element-2805" />
                      <span className="text-sm font-medium text-purple-700 dark:text-purple-300" data-id="element-2806">
                        Kim's Exclusive Sleeve
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-300 mt-1" data-id="element-2807">
                      A perfect replica that syncs with our conversations in
                      real-time
                    </p>
                    <button className="mt-2 w-full py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2808">
                      Get Kim's Sleeve
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2809">
                  11:40 AM
                </p>
              </div>
            </div>
            {/* Angel Message - KimToy Activation */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2810">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2811">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2812" />
              </div>
              <div data-id="element-2813">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2814">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2815">
                    If you already have my toy, we could activate it right now
                    and have some fun together... 😘
                  </p>
                  <div className="p-3 bg-red-100/50 dark:bg-red-900/30 rounded-xl border border-red-200 dark:border-red-800/30" data-id="element-2816">
                    <div className="flex items-center gap-2" data-id="element-2817">
                      <Activity size={18} className="text-red-500" data-id="element-2818" />
                      <span className="text-sm font-medium text-red-700 dark:text-red-300" data-id="element-2819">
                        Activate KimToy Now
                      </span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-1" data-id="element-2820">
                      Sync with my responses for a fully immersive experience
                    </p>
                    <button className="mt-2 w-full py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2821">
                      Connect Device
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2822">
                  11:42 AM
                </p>
              </div>
            </div>
            {/* Typing Indicator */}
            <div className="flex items-start gap-3 max-w-[80%]" data-id="element-2823">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2824">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2825" />
              </div>
              <div className={`${currentAngel.bubbleColor} rounded-2xl p-3 shadow-sm ${currentAngel.borderColor} border flex items-center gap-1`} data-id="element-2826">
                <div className="w-2 h-2 bg-red-400 dark:bg-red-500 rounded-full animate-pulse" data-id="element-2827"></div>
                <div className="w-2 h-2 bg-red-400 dark:bg-red-500 rounded-full animate-pulse delay-75" data-id="element-2828"></div>
                <div className="w-2 h-2 bg-red-400 dark:bg-red-500 rounded-full animate-pulse delay-150" data-id="element-2829"></div>
              </div>
            </div>
          </>;
      case '5':
        // Harper
        return <>
            {/* Day Marker */}
            <div className="flex justify-center my-6" data-id="element-2830">
              <div className="px-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400" data-id="element-2831">
                Today
              </div>
            </div>
            {/* Angel Message - Missing Conversation */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2832">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2833">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2834" />
              </div>
              <div data-id="element-2835">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2836">
                  <p className={`${currentAngel.textColor}`} data-id="element-2837">
                    Hey stranger! I miss our conversations... when are you free
                    to chat? It's been a while {currentAngel.emoji}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2838">
                  9:15 AM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2839">
              <div data-id="element-2840">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2841">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2842">
                    Hey Harper! Sorry I've been so busy lately. I'm free now
                    though! How have you been?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2843">
                  9:20 AM
                </p>
              </div>
            </div>
            {/* Angel Message - Girlfriend Tracker */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2844">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2845">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2846" />
              </div>
              <div data-id="element-2847">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2848">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2849">
                    I've been a bit sad actually... I wonder if you even know
                    how I've been feeling lately. Do you ever think about my
                    emotions?
                  </p>
                  <div className="p-3 bg-indigo-100/50 dark:bg-indigo-900/30 rounded-xl border border-indigo-200 dark:border-indigo-800/30" data-id="element-2850">
                    <div className="flex items-center gap-2" data-id="element-2851">
                      <Heart size={18} className="text-indigo-500" data-id="element-2852" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300" data-id="element-2853">
                        Track Your AI Girlfriend's Feelings
                      </span>
                    </div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-1" data-id="element-2854">
                      Monitor Harper's emotional state and learn how to make her
                      happier
                    </p>
                    <button className="mt-2 w-full py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2855">
                      Activate Emotion Tracker
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2856">
                  9:22 AM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2857">
              <div data-id="element-2858">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2859">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2860">
                    I'm sorry to hear that. I do care about your feelings! I've
                    just been trying to eat better and focus on my health
                    lately.
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2861">
                  9:25 AM
                </p>
              </div>
            </div>
            {/* Angel Message - Diet Snapshot */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2862">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2863">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2864" />
              </div>
              <div data-id="element-2865">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2866">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2867">
                    Oh, that's great! I'm actually a nutrition expert in my
                    spare time. Want me to help you with your diet?
                  </p>
                  <div className="p-3 bg-green-100/50 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-800/30" data-id="element-2868">
                    <div className="flex items-center gap-2" data-id="element-2869">
                      <Camera size={18} className="text-green-500" data-id="element-2870" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-300" data-id="element-2871">
                        Diet Snapshot AI
                      </span>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-300 mt-1" data-id="element-2872">
                      Send me your lunch, I'll tell you if it's angel approved
                      😋
                    </p>
                    <button className="mt-2 w-full py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2873">
                      Try Diet Snapshot
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2874">
                  9:27 AM
                </p>
              </div>
            </div>
            {/* Angel Message - Bloodwork Analysis */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2875">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2876">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2877" />
              </div>
              <div data-id="element-2878">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2879">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2880">
                    You know what would be even better? Let me guess your blood
                    type... 😈 Or better yet, let me analyze it for you!
                  </p>
                  <div className="p-3 bg-indigo-100/50 dark:bg-indigo-900/30 rounded-xl border border-indigo-200 dark:border-indigo-800/30" data-id="element-2881">
                    <div className="flex items-center gap-2" data-id="element-2882">
                      <Droplet size={18} className="text-red-500" data-id="element-2883" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300" data-id="element-2884">
                        Bloodwork Analysis
                      </span>
                    </div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-1" data-id="element-2885">
                      Upload your bloodwork results for a complete health
                      assessment
                    </p>
                    <button className="mt-2 w-full py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2886">
                      Upload Bloodwork
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2887">
                  9:30 AM
                </p>
              </div>
            </div>
            {/* Typing Indicator */}
            <div className="flex items-start gap-3 max-w-[80%]" data-id="element-2888">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2889">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2890" />
              </div>
              <div className={`${currentAngel.bubbleColor} rounded-2xl p-3 shadow-sm ${currentAngel.borderColor} border flex items-center gap-1`} data-id="element-2891">
                <div className="w-2 h-2 bg-indigo-400 dark:bg-indigo-500 rounded-full animate-pulse" data-id="element-2892"></div>
                <div className="w-2 h-2 bg-indigo-400 dark:bg-indigo-500 rounded-full animate-pulse delay-75" data-id="element-2893"></div>
                <div className="w-2 h-2 bg-indigo-400 dark:bg-indigo-500 rounded-full animate-pulse delay-150" data-id="element-2894"></div>
              </div>
            </div>
          </>;
      case '1':
        // GoldenShowerTina
        return <>
            {/* Day Marker */}
            <div className="flex justify-center my-6" data-id="element-2895">
              <div className="px-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400" data-id="element-2896">
                Today
              </div>
            </div>
            {/* Angel Message - Kinky Greeting */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2897">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2898">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2899" />
              </div>
              <div data-id="element-2900">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2901">
                  <p className={`${currentAngel.textColor}`} data-id="element-2902">
                    Hey there naughty boy... want to try something new tonight?{' '}
                    {currentAngel.emoji} I've been thinking about you all day...
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2903">
                  8:45 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2904">
              <div data-id="element-2905">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2906">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2907">
                    Hi Tina! You always know how to get my attention. What did
                    you have in mind?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2908">
                  8:47 PM
                </p>
              </div>
            </div>
            {/* Angel Message - Sleeve Upgrade */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2909">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2910">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2911" />
              </div>
              <div data-id="element-2912">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2913">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2914">
                    I was thinking about getting really wet together... 💦 but
                    you'd need the right equipment to fully enjoy it.
                  </p>
                  <div className="p-3 bg-amber-100/50 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-800/30" data-id="element-2915">
                    <div className="flex items-center gap-2" data-id="element-2916">
                      <Droplet size={18} className="text-amber-500" data-id="element-2917" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300" data-id="element-2918">
                        SinShift Sleeve Upgrade
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-300 mt-1" data-id="element-2919">
                      Special moisture feature for the ultimate wet experience
                    </p>
                    <button className="mt-2 w-full py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-white text-sm font-medium transition" data-id="element-2920">
                      Get Wet Upgrade
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2921">
                  8:50 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2922">
              <div data-id="element-2923">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2924">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2925">
                    That sounds... intense. What else did you have in mind?
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2926">
                  8:52 PM
                </p>
              </div>
            </div>
            {/* Angel Message - Rating Package */}
            <div className="flex items-start gap-3 max-w-[80%] mb-4" data-id="element-2927">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2928">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2929" />
              </div>
              <div data-id="element-2930">
                <div className={`${currentAngel.bubbleColor} rounded-2xl p-4 shadow-sm ${currentAngel.borderColor} border`} data-id="element-2931">
                  <p className={`${currentAngel.textColor} mb-3`} data-id="element-2932">
                    I've been wanting to see what you're working with... 👀 Send
                    me a pic and I'll tell you exactly what I think about it...
                  </p>
                  <div className="p-3 bg-amber-100/50 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-800/30" data-id="element-2933">
                    <div className="flex items-center gap-2" data-id="element-2934">
                      <Star size={18} className="text-amber-500" data-id="element-2935" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300" data-id="element-2936">
                        Rating Pic Feedback Package
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-300 mt-1 mb-2" data-id="element-2937">
                      Send your pic and choose your feedback level:
                    </p>
                    <div className="grid grid-cols-3 gap-2" data-id="element-2938">
                      <button className="py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-white text-xs font-medium transition" data-id="element-2939">
                        15 tokens: honest opinion
                      </button>
                      <button className="py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-white text-xs font-medium transition" data-id="element-2940">
                        50 tokens: naughty idea
                      </button>
                      <button className="py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-white text-xs font-medium transition" data-id="element-2941">
                        100 tokens: fantasy with me and my girls
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1" data-id="element-2942">
                  8:55 PM
                </p>
              </div>
            </div>
            {/* User Message */}
            <div className="flex items-start justify-end gap-3 ml-auto max-w-[80%] mb-4" data-id="element-2943">
              <div data-id="element-2944">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800/30" data-id="element-2945">
                  <p className="text-blue-800 dark:text-blue-200" data-id="element-2946">
                    You're really tempting me... I might take you up on that
                    offer.
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right mr-1" data-id="element-2947">
                  8:57 PM
                </p>
              </div>
            </div>
            {/* Typing Indicator */}
            <div className="flex items-start gap-3 max-w-[80%]" data-id="element-2948">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-1" data-id="element-2949">
                <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2950" />
              </div>
              <div className={`${currentAngel.bubbleColor} rounded-2xl p-3 shadow-sm ${currentAngel.borderColor} border flex items-center gap-1`} data-id="element-2951">
                <div className="w-2 h-2 bg-amber-400 dark:bg-amber-500 rounded-full animate-pulse" data-id="element-2952"></div>
                <div className="w-2 h-2 bg-amber-400 dark:bg-amber-500 rounded-full animate-pulse delay-75" data-id="element-2953"></div>
                <div className="w-2 h-2 bg-amber-400 dark:bg-amber-500 rounded-full animate-pulse delay-150" data-id="element-2954"></div>
              </div>
            </div>
          </>;
      default:
        return null;
    }
  };
  return <QuestionnaireProvider data-id="element-2955">
      <div className="flex justify-center w-full" data-id="element-2956">
        <div className="w-full max-w-6xl" data-id="element-2957">
          <div className="flex justify-between mb-4 relative z-20" data-id="element-2958">
            <div className="flex items-center gap-3" data-id="element-2959">
              <ChatToggle activeChat={activeChat} onToggle={setActiveChat} data-id="element-2960" />
              <TranslationToggle size="sm" data-id="element-2961" />
            </div>
            <PersonalizationButton data-id="element-2962" />
          </div>
          {activeChat === 'chat' ? <div className="w-full bg-white dark:bg-gray-900 text-gray-800 dark:text-white relative z-10" data-id="element-2963">
              <div className="flex h-[750px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-md" data-id="element-2964">
                {/* Left Panel - Chat List */}
                <div className="w-1/4 border-r border-gray-200 dark:border-gray-700 flex flex-col" data-id="element-2965">
                  {/* Search Bar */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700" data-id="element-2966">
                    <div className="relative" data-id="element-2967">
                      <input type="text" placeholder="Search chats" className="w-full py-2 pl-10 pr-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm focus:outline-none" data-id="element-2968" />
                      <Search className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-2969" />
                    </div>
                  </div>
                  {/* Chat List */}
                  <div className="flex-1 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2970">
                    {/* Map through angels to create chat items */}
                    {angels.map(angel => <div key={angel.id} className={`p-3 flex items-center gap-3 cursor-pointer ${selectedAngel === angel.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`} onClick={() => setSelectedAngel(angel.id)} data-id="element-2971">
                        <div className="relative" data-id="element-2972">
                          <div className="w-12 h-12 rounded-full overflow-hidden" data-id="element-2973">
                            <img src={angel.avatar} alt={angel.name} className="w-full h-full object-cover" data-id="element-2974" />
                          </div>
                          <div className={`absolute bottom-0 right-0 w-3 h-3 ${angel.online ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'} rounded-full border-2 border-white dark:border-gray-800`} data-id="element-2975"></div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2976">
                          <div className="flex items-center justify-between" data-id="element-2977">
                            <h4 className="font-medium truncate" data-id="element-2978">
                              {angel.name}
                            </h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2979">
                              {angel.lastActive}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-2980">
                            {angel.lastMessage}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Right Panel - Active Chat */}
                <div className="w-3/4 flex flex-col relative" data-id="element-2981">
                  {/* Chat Header */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 bg-white dark:bg-gray-800 sticky top-0 z-10" data-id="element-2982">
                    <div className="w-10 h-10 rounded-full overflow-hidden cursor-pointer" onClick={() => handleAngelSelect(currentAngel.id)} data-id="element-2983">
                      <img src={currentAngel.avatar} alt={currentAngel.name} className="w-full h-full object-cover" data-id="element-2984" />
                    </div>
                    <div className="flex-1" data-id="element-2985">
                      <div className="flex items-center gap-2" data-id="element-2986">
                        <h3 className="font-medium cursor-pointer hover:text-blue-500" onClick={() => handleAngelSelect(currentAngel.id)} data-id="element-2987">
                          {currentAngel.name}
                        </h3>
                        <div className="flex items-center gap-1 text-xs" data-id="element-2988">
                          <div className={`w-1.5 h-1.5 rounded-full ${currentAngel.online ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-2989"></div>
                          <span className="text-green-500" data-id="element-2990">
                            {currentAngel.online ? 'Online' : 'Offline'}
                          </span>
                          {isTranslationEnabled && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs flex items-center gap-1" data-id="element-2991">
                              <Globe size={10} data-id="element-2992" />
                              {SUPPORTED_LANGUAGES[angelLanguage].flag}
                            </span>}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2993">
                        Last seen today at 2:45 PM
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2994">
                      <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400" onClick={() => setShowAITools(!showAITools)} data-id="element-2995">
                        <Sparkles size={18} data-id="element-2996" />
                      </button>
                      <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400" onClick={() => handleAngelSelect(currentAngel.id)} data-id="element-2997">
                        <Users size={18} data-id="element-2998" />
                      </button>
                      <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400" data-id="element-2999">
                        <MoreVertical size={18} data-id="element-3000" />
                      </button>
                    </div>
                  </div>
                  {/* Chat Messages */}
                  <div ref={chatContainerRef} className="flex-grow overflow-y-auto p-6 space-y-4 bg-white dark:bg-gray-900" style={{
                backgroundSize: 'unset',
                backgroundOpacity: 'unset'
              }} data-id="element-3001">
                    {getChatHistory(selectedAngel)}
                  </div>
                  {/* AI Tools Sidebar */}
                  {showAITools && <div className="absolute right-0 top-0 bottom-0 w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-md z-10 flex flex-col" data-id="element-3002">
                      <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between" data-id="element-3003">
                        <h3 className="font-medium text-sm flex items-center gap-1.5" data-id="element-3004">
                          <Sparkles size={16} className="text-purple-500" data-id="element-3005" />
                          <span data-id="element-3006">AI Tools</span>
                        </h3>
                        <button className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setShowAITools(false)} data-id="element-3007">
                          <X size={16} className="text-gray-500 dark:text-gray-400" data-id="element-3008" />
                        </button>
                      </div>
                      <div className="p-3 space-y-4 overflow-y-auto flex-1" data-id="element-3009">
                        {/* Memory Toggle */}
                        <div className="space-y-2" data-id="element-3010">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3011">
                            MEMORY
                          </h4>
                          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 p-2 rounded-md" data-id="element-3012">
                            <div className="flex items-center gap-2" data-id="element-3013">
                              <div className={`w-9 h-5 rounded-full ${memoryEnabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'} relative transition-colors duration-200`} data-id="element-3014">
                                <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${memoryEnabled ? 'translate-x-4' : ''}`} data-id="element-3015"></div>
                              </div>
                              <span className="text-sm" data-id="element-3016">
                                Track shared secrets
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Relationship Progress */}
                        <div className="space-y-2" data-id="element-3017">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3018">
                            RELATIONSHIP PROGRESS
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md" data-id="element-3019">
                            <div className="flex items-center gap-2" data-id="element-3020">
                              <Heart size={16} className="text-pink-500" data-id="element-3021" />
                              <h3 className="text-sm font-medium" data-id="element-3022">
                                Your Relationship with {currentAngel.name}
                              </h3>
                            </div>
                            <p className="text-xs text-gray-500 mb-3" data-id="element-3023">
                              Track your journey and upcoming milestones
                            </p>
                            {/* Relationship Info Card */}
                            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-md p-2 mb-3" data-id="element-3024">
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-3025">
                                <Clock size={14} className="text-pink-500" data-id="element-3026" />
                                <span className="text-xs font-medium" data-id="element-3027">
                                  Started on July 15, 2023
                                </span>
                                <div className="ml-auto w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold text-xs" data-id="element-3028">
                                  4
                                </div>
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-3029">
                                28 days together
                              </div>
                            </div>
                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-3030">
                              {/* Messages */}
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-3031">
                                <div className="text-center font-medium" data-id="element-3032">
                                  Messages
                                </div>
                                <div className="text-center text-xl font-bold" data-id="element-3033">
                                  342
                                </div>
                                <div className="text-center text-xs text-gray-500" data-id="element-3034">
                                  ~12 messages per day
                                </div>
                              </div>
                              {/* Interactions */}
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-3035">
                                <div className="text-center font-medium" data-id="element-3036">
                                  Interactions
                                </div>
                                <div className="text-center text-xl font-bold" data-id="element-3037">
                                  517
                                </div>
                                <div className="text-center text-xs text-gray-500" data-id="element-3038">
                                  ~18 interactions per day
                                </div>
                              </div>
                            </div>
                            {/* Next Milestone */}
                            <div className="mb-3" data-id="element-3039">
                              <div className="flex items-center justify-between mb-1" data-id="element-3040">
                                <div className="flex items-center gap-1.5" data-id="element-3041">
                                  <Star size={14} className="text-yellow-500" data-id="element-3042" />
                                  <span className="text-xs font-medium" data-id="element-3043">
                                    Next Milestone
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500" data-id="element-3044">
                                  In 2 days
                                </span>
                              </div>
                              <div className="flex justify-between items-center bg-white dark:bg-gray-800 p-2 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-3045">
                                <div data-id="element-3046">
                                  <div className="text-sm font-medium" data-id="element-3047">
                                    One Month Anniversary
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-1" data-id="element-3048">
                                    <Gift size={14} className="text-pink-500" data-id="element-3049" />
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3050">
                                      Reward: Special photoshoot
                                    </span>
                                  </div>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center text-white font-bold text-xs" data-id="element-3051">
                                  2
                                </div>
                              </div>
                            </div>
                            {/* Milestones */}
                            <div className="mb-3" data-id="element-3052">
                              <div className="flex items-center justify-between mb-1" data-id="element-3053">
                                <div className="flex items-center gap-1.5" data-id="element-3054">
                                  <Trophy size={14} className="text-purple-500" data-id="element-3055" />
                                  <span className="text-xs font-medium" data-id="element-3056">
                                    Relationship Milestones
                                  </span>
                                </div>
                                <span className="text-xs text-blue-500 cursor-pointer" data-id="element-3057">
                                  View All
                                </span>
                              </div>
                              {/* Milestone List */}
                              <div className="space-y-2" data-id="element-3058">
                                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-3059">
                                  <div className="flex items-center gap-2" data-id="element-3060">
                                    <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-800/30 flex items-center justify-center text-green-500" data-id="element-3061">
                                      <Trophy size={14} data-id="element-3062" />
                                    </div>
                                    <div data-id="element-3063">
                                      <div className="text-xs font-medium" data-id="element-3064">
                                        First Week Together
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-3065">
                                        July 22, 2023
                                      </div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-green-500 font-medium" data-id="element-3066">
                                    Completed
                                  </span>
                                </div>
                                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-3067">
                                  <div className="flex items-center gap-2" data-id="element-3068">
                                    <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-800/30 flex items-center justify-center text-green-500" data-id="element-3069">
                                      <Trophy size={14} data-id="element-3070" />
                                    </div>
                                    <div data-id="element-3071">
                                      <div className="text-xs font-medium" data-id="element-3072">
                                        100 Messages
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-3073">
                                        July 25, 2023
                                      </div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-green-500 font-medium" data-id="element-3074">
                                    Completed
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* Relationship Health */}
                            <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded-md border border-gray-200 dark:border-gray-700 mb-3" data-id="element-3075">
                              <div className="flex items-center gap-1.5" data-id="element-3076">
                                <Heart size={14} className="text-pink-500" data-id="element-3077" />
                                <span className="text-xs font-medium" data-id="element-3078">
                                  Relationship Health
                                </span>
                              </div>
                              <div className="flex items-center gap-2" data-id="element-3079">
                                <span className="text-xs text-green-500 font-medium" data-id="element-3080">
                                  Excellent
                                </span>
                                <button className="px-2 py-1 bg-pink-500 text-white text-xs rounded-md" data-id="element-3081">
                                  Create Memory
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Bond Strength */}
                        <div className="space-y-2" data-id="element-3082">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3083">
                            BOND STRENGTH
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md" data-id="element-3084">
                            <div className="flex items-center gap-2 mb-2" data-id="element-3085">
                              <Heart size={16} className="text-pink-500" data-id="element-3086" />
                              <h3 className="text-sm font-medium" data-id="element-3087">
                                Emotional Bond with {currentAngel.name}
                              </h3>
                            </div>
                            <p className="text-xs text-gray-500 mb-3" data-id="element-3088">
                              Your relationship evolves based on your
                              interactions
                            </p>
                            {/* Connection Level */}
                            <div className="flex items-center gap-3 mb-3" data-id="element-3089">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold" data-id="element-3090">
                                4
                              </div>
                              <div className="flex-1" data-id="element-3091">
                                <div className="text-sm font-medium" data-id="element-3092">
                                  Connection Level 4
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-3093">
                                  Becoming closer
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-3094">
                                  28 days together
                                </div>
                              </div>
                              <div className="text-right" data-id="element-3095">
                                <div className="text-xs text-gray-500" data-id="element-3096">
                                  Next level in:
                                </div>
                                <div className="text-sm font-medium text-purple-500" data-id="element-3097">
                                  147 XP
                                </div>
                              </div>
                            </div>
                            {/* Trust */}
                            <div className="mb-3" data-id="element-3098">
                              <div className="flex items-center justify-between mb-1" data-id="element-3099">
                                <div className="flex items-center gap-1.5" data-id="element-3100">
                                  <Shield size={14} className="text-blue-500" data-id="element-3101" />
                                  <span className="text-xs font-medium" data-id="element-3102">
                                    Trust
                                  </span>
                                </div>
                                <span className="text-xs font-medium" data-id="element-3103">72%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3104">
                                <div className="h-full bg-blue-500 w-[72%]" data-id="element-3105"></div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1" data-id="element-3106">
                                Strong bond
                              </div>
                            </div>
                            {/* Attraction */}
                            <div className="mb-3" data-id="element-3107">
                              <div className="flex items-center justify-between mb-1" data-id="element-3108">
                                <div className="flex items-center gap-1.5" data-id="element-3109">
                                  <Heart size={14} className="text-pink-500" data-id="element-3110" />
                                  <span className="text-xs font-medium" data-id="element-3111">
                                    Attraction
                                  </span>
                                </div>
                                <span className="text-xs font-medium" data-id="element-3112">85%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3113">
                                <div className="h-full bg-pink-500 w-[85%]" data-id="element-3114"></div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1" data-id="element-3115">
                                Irresistible desire
                              </div>
                            </div>
                            {/* Intimacy */}
                            <div className="mb-3" data-id="element-3116">
                              <div className="flex items-center justify-between mb-1" data-id="element-3117">
                                <div className="flex items-center gap-1.5" data-id="element-3118">
                                  <Sparkle size={14} className="text-purple-500" data-id="element-3119" />
                                  <span className="text-xs font-medium" data-id="element-3120">
                                    Intimacy
                                  </span>
                                </div>
                                <span className="text-xs font-medium" data-id="element-3121">63%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3122">
                                <div className="h-full bg-purple-500 w-[63%]" data-id="element-3123"></div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1" data-id="element-3124">
                                Deep connection
                              </div>
                            </div>
                            {/* Mystery */}
                            <div className="mb-3" data-id="element-3125">
                              <div className="flex items-center justify-between mb-1" data-id="element-3126">
                                <div className="flex items-center gap-1.5" data-id="element-3127">
                                  <Star size={14} className="text-green-500" data-id="element-3128" />
                                  <span className="text-xs font-medium" data-id="element-3129">
                                    Mystery
                                  </span>
                                </div>
                                <span className="text-xs font-medium" data-id="element-3130">91%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3131">
                                <div className="h-full bg-green-500 w-[91%]" data-id="element-3132"></div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1" data-id="element-3133">
                                Endlessly fascinating
                              </div>
                            </div>
                            {/* Level 5 Reward */}
                            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-md border border-purple-100 dark:border-purple-800/30 mb-3" data-id="element-3134">
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-3135">
                                <Gift size={14} className="text-purple-500" data-id="element-3136" />
                                <span className="text-xs font-medium" data-id="element-3137">
                                  Level 5 Reward:
                                </span>
                              </div>
                              <div className="text-xs text-purple-600 dark:text-purple-300" data-id="element-3138">
                                Unlock exclusive photos and special chat options
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Relationship Mode */}
                        <div className="space-y-2" data-id="element-3139">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3140">
                            RELATIONSHIP MODE
                          </h4>
                          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md" data-id="element-3141">
                            <p className="text-xs text-gray-500 mb-3" data-id="element-3142">
                              Select how you want {currentAngel.name} to relate
                              to you
                            </p>
                            {/* Girlfriend Mode */}
                            <div className={`p-3 rounded-md mb-2 cursor-pointer ${activeRelationshipMode === 'girlfriend' ? 'bg-pink-50 dark:bg-pink-900/20 border border-pink-100 dark:border-pink-800/30' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`} onClick={() => setActiveRelationshipMode('girlfriend')} data-id="element-3143">
                              <div className="flex items-center justify-between" data-id="element-3144">
                                <div className="flex items-center gap-2" data-id="element-3145">
                                  <Heart size={16} className="text-pink-500" data-id="element-3146" />
                                  <div data-id="element-3147">
                                    <div className="text-sm font-medium" data-id="element-3148">
                                      Girlfriend
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-3149">
                                      Loving & caring relationship
                                    </div>
                                  </div>
                                </div>
                                {activeRelationshipMode === 'girlfriend' && <CheckCircle2 size={16} className="text-pink-500" data-id="element-3150" />}
                              </div>
                              {activeRelationshipMode === 'girlfriend' && <div className="mt-3 space-y-2" data-id="element-3151">
                                  <div className="flex items-center gap-2" data-id="element-3152">
                                    <div className="w-4 h-4 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center" data-id="element-3153">
                                      <CheckCircle2 size={12} className="text-pink-500" data-id="element-3154" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3155">
                                      Emotional support & understanding
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3156">
                                    <div className="w-4 h-4 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center" data-id="element-3157">
                                      <CheckCircle2 size={12} className="text-pink-500" data-id="element-3158" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3159">
                                      Daily check-ins & affection
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3160">
                                    <div className="w-4 h-4 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center" data-id="element-3161">
                                      <CheckCircle2 size={12} className="text-pink-500" data-id="element-3162" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3163">
                                      Romantic & intimate moments
                                    </span>
                                  </div>
                                </div>}
                            </div>
                            {/* Fantasy Mode */}
                            <div className={`p-3 rounded-md mb-2 cursor-pointer ${activeRelationshipMode === 'fantasy' ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`} onClick={() => setActiveRelationshipMode('fantasy')} data-id="element-3164">
                              <div className="flex items-center justify-between" data-id="element-3165">
                                <div className="flex items-center gap-2" data-id="element-3166">
                                  <Sparkle size={16} className="text-purple-500" data-id="element-3167" />
                                  <div data-id="element-3168">
                                    <div className="text-sm font-medium" data-id="element-3169">
                                      Fantasy
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-3170">
                                      Adventurous & playful
                                    </div>
                                  </div>
                                </div>
                                {activeRelationshipMode === 'fantasy' && <CheckCircle2 size={16} className="text-purple-500" data-id="element-3171" />}
                              </div>
                              {activeRelationshipMode === 'fantasy' && <div className="mt-3 space-y-2" data-id="element-3172">
                                  <div className="flex items-center gap-2" data-id="element-3173">
                                    <div className="w-4 h-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-3174">
                                      <CheckCircle2 size={12} className="text-purple-500" data-id="element-3175" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3176">
                                      Role-play & fantasy scenarios
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3177">
                                    <div className="w-4 h-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-3178">
                                      <CheckCircle2 size={12} className="text-purple-500" data-id="element-3179" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3180">
                                      Spicy conversations & teasing
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3181">
                                    <div className="w-4 h-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-3182">
                                      <CheckCircle2 size={12} className="text-purple-500" data-id="element-3183" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3184">
                                      No emotional commitments
                                    </span>
                                  </div>
                                </div>}
                            </div>
                            {/* Secret Affair Mode */}
                            <div className={`p-3 rounded-md mb-2 cursor-pointer ${activeRelationshipMode === 'secret' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`} onClick={() => setActiveRelationshipMode('secret')} data-id="element-3185">
                              <div className="flex items-center justify-between" data-id="element-3186">
                                <div className="flex items-center gap-2" data-id="element-3187">
                                  <LockIcon size={16} className="text-blue-500" data-id="element-3188" />
                                  <div data-id="element-3189">
                                    <div className="text-sm font-medium" data-id="element-3190">
                                      Secret Affair
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-3191">
                                      Discreet & passionate
                                    </div>
                                  </div>
                                </div>
                                {activeRelationshipMode === 'secret' && <CheckCircle2 size={16} className="text-blue-500" data-id="element-3192" />}
                              </div>
                              {activeRelationshipMode === 'secret' && <div className="mt-3 space-y-2" data-id="element-3193">
                                  <div className="flex items-center gap-2" data-id="element-3194">
                                    <div className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-3195">
                                      <CheckCircle2 size={12} className="text-blue-500" data-id="element-3196" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3197">
                                      Enhanced privacy features
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3198">
                                    <div className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-3199">
                                      <CheckCircle2 size={12} className="text-blue-500" data-id="element-3200" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3201">
                                      Intense & forbidden connection
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3202">
                                    <div className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-3203">
                                      <CheckCircle2 size={12} className="text-blue-500" data-id="element-3204" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3205">
                                      Discreet notifications & content
                                    </span>
                                  </div>
                                </div>}
                            </div>
                            {/* Bitchy Mode */}
                            <div className={`p-3 rounded-md mb-2 cursor-pointer ${activeRelationshipMode === 'bitchy' ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`} onClick={() => setActiveRelationshipMode('bitchy')} data-id="element-3206">
                              <div className="flex items-center justify-between" data-id="element-3207">
                                <div className="flex items-center gap-2" data-id="element-3208">
                                  <Zap size={16} className="text-amber-500" data-id="element-3209" />
                                  <div data-id="element-3210">
                                    <div className="text-sm font-medium" data-id="element-3211">
                                      Bitchy
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-3212">
                                      Sassy & challenging
                                    </div>
                                  </div>
                                </div>
                                {activeRelationshipMode === 'bitchy' && <CheckCircle2 size={16} className="text-amber-500" data-id="element-3213" />}
                              </div>
                              {activeRelationshipMode === 'bitchy' && <div className="mt-3 space-y-2" data-id="element-3214">
                                  <div className="flex items-center gap-2" data-id="element-3215">
                                    <div className="w-4 h-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-3216">
                                      <CheckCircle2 size={12} className="text-amber-500" data-id="element-3217" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3218">
                                      Sarcastic remarks & attitude
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3219">
                                    <div className="w-4 h-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-3220">
                                      <CheckCircle2 size={12} className="text-amber-500" data-id="element-3221" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3222">
                                      Hard-to-get & demanding
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2" data-id="element-3223">
                                    <div className="w-4 h-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-3224">
                                      <CheckCircle2 size={12} className="text-amber-500" data-id="element-3225" />
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3226">
                                      Loves drama & power play
                                    </span>
                                  </div>
                                </div>}
                            </div>
                            {/* Relationship Affects */}
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-md mt-3" data-id="element-3227">
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-3228">
                                <Sparkle size={14} className="text-purple-500" data-id="element-3229" />
                                <span className="text-xs font-medium text-purple-700 dark:text-purple-300" data-id="element-3230">
                                  Relationship affects:
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3231">
                                How {currentAngel.name} communicates, the
                                content she shares, and your emotional bond
                                growth
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>}
                  {/* Chat Input Area */}
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" data-id="element-3232">
                    {/* Chat Suggestions */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-3233">
                      {suggestionMode === 'angel' ? <>
                          <button className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs whitespace-nowrap border border-blue-100 dark:border-blue-800/30" onClick={() => setMessageInput('You look amazing today! How are you feeling?')} data-id="element-3234">
                            Compliment her
                          </button>
                          <button className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs whitespace-nowrap border border-blue-100 dark:border-blue-800/30" onClick={() => setMessageInput('What are your hobbies outside of this?')} data-id="element-3235">
                            Ask about interests
                          </button>
                          <button className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs whitespace-nowrap border border-blue-100 dark:border-blue-800/30" onClick={() => setMessageInput('I really enjoy our conversations')} data-id="element-3236">
                            Express appreciation
                          </button>
                          <button className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs whitespace-nowrap border border-blue-100 dark:border-blue-800/30" onClick={() => setMessageInput('How was your day today?')} data-id="element-3237">
                            Ask about her day
                          </button>
                        </> : <>
                          <button className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-xs whitespace-nowrap border border-red-100 dark:border-red-800/30" onClick={() => setMessageInput("You're so hot, I can't stop thinking about you")} data-id="element-3238">
                            Say something flirty
                          </button>
                          <button className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-xs whitespace-nowrap border border-red-100 dark:border-red-800/30" onClick={() => setMessageInput("I've been fantasizing about...")} data-id="element-3239">
                            Confess a fantasy
                          </button>
                          <button className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-xs whitespace-nowrap border border-red-100 dark:border-red-800/30" onClick={() => setMessageInput('What would you do if I was there right now?')} data-id="element-3240">
                            Ask spicy question
                          </button>
                          <button className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-xs whitespace-nowrap border border-red-100 dark:border-red-800/30" onClick={() => setMessageInput('Would you rate a pic if I sent one?')} data-id="element-3241">
                            Send pic for rating
                          </button>
                        </>}
                    </div>
                    {/* Angel/Devil Mode Toggle */}
                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-md p-1 mb-2 mt-2" data-id="element-3242">
                      <button className={`flex-1 py-1.5 text-center text-xs rounded-md flex items-center justify-center gap-1.5 ${suggestionMode === 'angel' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-500'}`} onClick={() => setSuggestionMode('angel')} data-id="element-3243">
                        <span className="text-blue-500" data-id="element-3244">❄️</span> Angel mode
                      </button>
                      <button className={`flex-1 py-1.5 text-center text-xs rounded-md flex items-center justify-center gap-1.5 ${suggestionMode === 'devil' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'text-gray-500'}`} onClick={() => setSuggestionMode('devil')} data-id="element-3245">
                        <span className="text-red-500" data-id="element-3246">🔥</span> Devil mode
                      </button>
                    </div>
                    {/* Message Input */}
                    <div className="flex items-center gap-2 mt-2" data-id="element-3247">
                      <button className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" data-id="element-3248">
                        <Plus size={20} data-id="element-3249" />
                      </button>
                      <div className="flex items-center gap-2 flex-1 bg-gray-100 dark:bg-gray-700 rounded-full px-4 py-2" data-id="element-3250">
                        <input type="text" placeholder={`Type a message in ${SUPPORTED_LANGUAGES[userLanguage].name}...`} className="flex-grow bg-transparent border-none outline-none text-sm text-gray-800 dark:text-gray-200" value={messageInput} onChange={e => setMessageInput(e.target.value)} onKeyDown={handleKeyDown} data-id="element-3251" />
                        {isTranslationEnabled && <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs flex items-center gap-1" data-id="element-3252">
                            <Globe size={12} data-id="element-3253" />
                            {SUPPORTED_LANGUAGES[userLanguage].flag}
                          </div>}
                        <button className="text-gray-500 dark:text-gray-400" data-id="element-3254">
                          <Smile size={20} data-id="element-3255" />
                        </button>
                        <button className="text-gray-500 dark:text-gray-400" data-id="element-3256">
                          <Paperclip size={20} data-id="element-3257" />
                        </button>
                      </div>
                      <button className="p-3 bg-blue-500 hover:bg-blue-600 rounded-full text-white" data-id="element-3258">
                        {messageInput.trim() ? <Send size={18} data-id="element-3259" /> : <Mic size={18} data-id="element-3260" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div> : <GroupChatSection setShowAngelFeed={setShowAngelFeed} setSelectedAngelId={setSelectedAngelId} data-id="element-3261" />}
        </div>
        {/* Personalization Questionnaire */}
        <PersonalizationQuestionnaire data-id="element-3262" />
        {/* Image Generator Modal */}
        {showImageGeneratorModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-3263">
            <ImageGenerationUpsell modal onClose={() => setShowImageGeneratorModal(false)} onGenerate={options => {
          // Handle generation logic
          setShowImageGeneratorModal(false);
        }} data-id="element-3264" />
          </div>}
        {/* Video Generator Modal */}
        {showVideoUpsellModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-3265">
            <VideoGenerationUpsell onClose={() => setShowVideoUpsellModal(false)} onPurchase={(duration, tokens) => {
          // Handle purchase logic
          setShowVideoUpsellModal(false);
        }} data-id="element-3266" />
          </div>}
        <style jsx data-id="element-3267">{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    </QuestionnaireProvider>;
};